/**
 * Surveys API
 *
 * Handles survey CRUD operations.
 */

import { api } from './client'

/**
 * Create a new survey
 *
 * @param {Object} params
 * @param {string} params.title - Survey title
 * @param {Object} params.definition - Form JSON definition (title, description, questions[])
 * @param {boolean} [params.publish=true] - Publish immediately
 * @param {Object} [params.settings] - Survey settings
 * @param {string} [params.organization_id] - Organization ID to associate survey with
 * @param {string} [params.default_language='en'] - Default survey language (BCP 47 code)
 * @param {'ltr'|'rtl'} [params.text_direction='ltr'] - Text direction for RTL languages
 * @param {string[]} [params.available_languages] - Languages available for multilingual surveys
 * @param {string[]} [params.allowed_response_types=['text']] - Allowed response types (text, audio, video, image)
 * @returns {Promise<Object>} Created survey with share_url
 */
export async function createSurvey({
  title,
  definition,
  publish = true,
  settings = {},
  organization_id,
  default_language,
  text_direction,
  available_languages,
  allowed_response_types,
}) {
  const body = {
    title,
    definition,
    publish,
    settings,
  }

  // Add organization if provided
  if (organization_id) body.organization_id = organization_id

  // Add language/i18n fields if provided
  if (default_language) body.default_language = default_language
  if (text_direction) body.text_direction = text_direction
  if (available_languages) body.available_languages = available_languages
  if (allowed_response_types) body.allowed_response_types = allowed_response_types

  return api.post('/surveys', body)
}

/**
 * List surveys with optional filtering
 *
 * @param {Object} [params]
 * @param {string} [params.organization_id] - Filter by organization
 * @param {string} [params.status] - Filter by status (draft, published, archived)
 * @param {string} [params.search] - Search in title
 * @param {number} [params.limit=25] - Max results (1-100)
 * @param {number} [params.offset=0] - Pagination offset
 * @returns {Promise<Object>} Paginated list of surveys
 */
export async function listSurveys({
  organization_id,
  status,
  search,
  limit = 25,
  offset = 0,
} = {}) {
  return api.get('/surveys', { organization_id, status, search, limit, offset })
}

/**
 * Get a survey by ID
 *
 * @param {string} surveyId - Survey UUID
 * @returns {Promise<Object>} Full survey object with definition
 */
export async function getSurvey(surveyId) {
  return api.get(`/surveys/${surveyId}`)
}

/**
 * Update a survey
 *
 * @param {string} surveyId - Survey UUID
 * @param {Object} updates
 * @param {string} [updates.title] - New title
 * @param {Object} [updates.definition] - New definition
 * @param {string} [updates.status] - New status
 * @param {Object} [updates.settings] - New settings
 * @param {string} [updates.default_language] - Default survey language
 * @param {'ltr'|'rtl'} [updates.text_direction] - Text direction
 * @param {string[]} [updates.available_languages] - Available languages
 * @param {string[]} [updates.allowed_response_types] - Allowed response types
 * @param {boolean} [updates.requires_field_agent] - Whether survey requires field agent links
 * @param {boolean} [updates.is_active] - Whether survey is active (only owner can change)
 * @returns {Promise<Object>} Updated survey
 */
export async function updateSurvey(
  surveyId,
  {
    title,
    definition,
    status,
    settings,
    theme,
    organization_id,
    default_language,
    text_direction,
    available_languages,
    allowed_response_types,
    requires_field_agent,
    is_active,
  }
) {
  const body = {}
  if (title !== undefined) body.title = title
  if (definition !== undefined) body.definition = definition
  if (status !== undefined) body.status = status
  if (settings !== undefined) body.settings = settings
  if (theme !== undefined) body.theme = theme
  if (organization_id !== undefined) body.organization_id = organization_id
  if (default_language !== undefined) body.default_language = default_language
  if (text_direction !== undefined) body.text_direction = text_direction
  if (available_languages !== undefined) body.available_languages = available_languages
  if (allowed_response_types !== undefined) body.allowed_response_types = allowed_response_types
  if (requires_field_agent !== undefined) body.requires_field_agent = requires_field_agent
  if (is_active !== undefined) body.is_active = is_active

  return api.patch(`/surveys/${surveyId}`, body)
}

/**
 * Delete a survey
 *
 * @param {string} surveyId - Survey UUID
 * @returns {Promise<Object>} Success message
 */
export async function deleteSurvey(surveyId) {
  return api.delete(`/surveys/${surveyId}`)
}

/**
 * Publish a survey
 *
 * @param {string} surveyId - Survey UUID
 * @returns {Promise<Object>} Updated survey with published status
 */
export async function publishSurvey(surveyId) {
  return api.post(`/surveys/${surveyId}/publish`)
}

/**
 * Archive a survey
 *
 * @param {string} surveyId - Survey UUID
 * @returns {Promise<Object>} Updated survey with archived status
 */
export async function archiveSurvey(surveyId) {
  return api.post(`/surveys/${surveyId}/archive`)
}

/**
 * Schedule a survey with start and end dates
 *
 * @param {string} surveyId - Survey UUID
 * @param {Object} schedule
 * @param {string} schedule.start_date - ISO date string for when survey starts
 * @param {string} schedule.end_date - ISO date string for when survey ends
 * @returns {Promise<Object>} Updated survey with schedule
 */
export async function scheduleSurvey(surveyId, { start_date, end_date }) {
  return api.patch(`/surveys/${surveyId}/schedule`, {
    start_date,
    end_date,
  })
}
